import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageWrapper from '../components/PageWrapper';
import MarketingSection from '../components/marketing/MarketingSection';
import SectionEyebrow from '../components/marketing/SectionEyebrow';
import FadeIn from '../components/marketing/FadeIn';
import FormSuccessPanel from '../components/marketing/FormSuccessPanel';
import { FloatingInput, FloatingSelect } from '../components/marketing/FloatingField';
import { EASE_REFINED } from '../components/marketing/constants';

const FORMSPREE_FORM_ID = process.env.REACT_APP_FORMSPREE_FORM_ID || 'mpqeoddl';

const COMPANY_SIZE_OPTIONS = [
  { value: '1-10', label: '1–10 employees' },
  { value: '11-50', label: '11–50 employees' },
  { value: '51-200', label: '51–200 employees' },
  { value: '201-500', label: '201–500 employees' },
  { value: '500+', label: '500+ employees' },
];

const benefits = [
  {
    title: 'Tailored risk snapshot',
    desc: 'See the retention signals that matter most for your team size — before anyone puts in notice.',
  },
  {
    title: 'No HRIS required',
    desc: 'A short intake is all we need for the pilot. No integrations, no IT project.',
  },
  {
    title: 'Actionable playbook',
    desc: 'Walk away with concrete next steps — not just scores on a dashboard.',
  },
];

const TRUST_BADGES = ['Free pilot', 'No commitment', 'Confidential'];

function PilotFormPanel() {
  const [submitted, setSubmitted] = useState(
    () => new URLSearchParams(window.location.search).get('submitted') === '1'
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const formspreeAction = `https://formspree.io/f/${FORMSPREE_FORM_ID}`;

  useEffect(() => {
    if (!submitted) return;
    window.history.replaceState(null, '', '/schedule-demo');
  }, [submitted]);

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitError('');
    setSubmitting(true);

    try {
      const form = event.currentTarget;
      const response = await fetch(formspreeAction, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error('submit-failed');
      }

      setSubmitted(true);
    } catch {
      setSubmitError('Something went wrong sending your request. Please try again or email us directly.');
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <FormSuccessPanel
        title="Your pilot request is in."
        message="Thanks for reaching out. We'll review your details and follow up within two business days with your free retention risk report. Check your inbox — and your spam or promotions folder — in case our reply lands there."
      />
    );
  }

  return (
    <div className="glass-panel marketing-form-panel">
      <div className="marketing-form-panel__label">Request your report</div>
      <p className="marketing-form-intro">
        Tell us a bit about your team so we can tailor your report. All fields below are required.
      </p>
      <form onSubmit={handleSubmit}>
        <input type="hidden" name="_subject" value="Stavyn — Free Risk Report Pilot Request" />
        <input type="hidden" name="formType" value="free-risk-report-pilot" />
        <input type="hidden" name="leadSource" value="paid-ad" />

        <FloatingInput label="Full Name" name="name" required />
        <FloatingInput label="Work Email" type="email" name="email" required />
        <FloatingInput label="Phone Number" type="tel" name="phone" required />
        <FloatingInput label="Company Name" name="company" required />
        <FloatingInput label="Job Title" name="jobTitle" required />
        <FloatingSelect
          label="Company Size"
          name="companySize"
          options={COMPANY_SIZE_OPTIONS}
          placeholder="Select company size"
          required
        />

        <p className="marketing-form-spam-note">
          After you submit, watch for an email from us within two business days. If you do not see it, check your spam or promotions folder and add our sender to your contacts.
        </p>

        {submitError ? (
          <p className="marketing-form-error" role="alert">
            {submitError}
          </p>
        ) : null}

        <motion.button
          type="submit"
          disabled={submitting}
          className="landing-cta landing-cta--hero marketing-form-submit"
          whileHover={!submitting ? { y: -2 } : {}}
          whileTap={!submitting ? { scale: 0.98 } : {}}
          transition={{ duration: 0.3, ease: EASE_REFINED }}
          style={{
            justifyContent: 'center',
            width: '100%',
            cursor: submitting ? 'wait' : 'pointer',
            opacity: submitting ? 0.7 : 1,
          }}
        >
          {submitting ? 'Submitting…' : 'Get My Free Risk Report'}
        </motion.button>
        <p className="marketing-form-footnote">
          Free pilot. Confidential.{' '}
          <Link to="/privacy" className="marketing-form-footnote__link">
            Privacy Policy
          </Link>
        </p>
      </form>
    </div>
  );
}

export default function ScheduleDemoPage() {
  return (
    <PageWrapper>
      <MarketingSection padding="sm" maxWidth="6xl" className="marketing-section--page-top">
        <FadeIn delay={0.05}>
          <SectionEyebrow className="mb-8 lg:mb-10">Free Risk Report Pilot</SectionEyebrow>
        </FadeIn>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 xl:gap-20 items-start">
          <div className="order-2 lg:order-1 flex flex-col gap-10 lg:gap-12">
            <div>
              <FadeIn delay={0.12}>
                <h1
                  className="landing-heading"
                  style={{
                    fontSize: 'clamp(2.25rem, 4.5vw, 3.75rem)',
                    lineHeight: 1.08,
                    letterSpacing: '-0.035em',
                    color: 'var(--text-primary)',
                    marginBottom: '1.5rem',
                  }}
                >
                  See your team's{' '}
                  <span className="headline-accent hero-headline-accent">flight risk.</span>
                </h1>
              </FadeIn>
              <FadeIn delay={0.2}>
                <p className="marketing-hero__subtitle" style={{ maxWidth: 480, marginBottom: '2rem' }}>
                  You clicked through for a complimentary retention risk report — we'll tailor it to your company and team size.
                </p>
              </FadeIn>
              <FadeIn delay={0.28}>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                  {TRUST_BADGES.map((badge) => (
                    <span key={badge} className="marketing-trust-badge">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                        <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="0.75" />
                        <path d="M3.5 6l2 2 3-3" stroke="currentColor" strokeWidth="0.875" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {badge}
                    </span>
                  ))}
                </div>
              </FadeIn>
            </div>

            <FadeIn delay={0.1}>
              <div>
                <div className="marketing-form-panel__label">What you'll receive</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                  {benefits.map((b, i) => (
                    <motion.div
                      key={b.title}
                      className="marketing-benefit-row"
                      initial={{ opacity: 0, x: -12 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.55, ease: EASE_REFINED, delay: 0.12 + i * 0.1 }}
                    >
                      <span className="marketing-benefit-row__num" aria-hidden="true">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <div>
                        <div className="marketing-benefit-row__title">{b.title}</div>
                        <div className="marketing-benefit-row__desc">{b.desc}</div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>

          <div className="order-1 lg:order-2">
            <FadeIn delay={0.1}>
              <PilotFormPanel />
            </FadeIn>
          </div>
        </div>

        <FadeIn delay={0.15} className="mt-12 lg:mt-16">
          <div className="glass-panel marketing-quote-card">
            <p className="marketing-quote-card__text">
              "Most teams only see retention risk after someone resigns. This pilot gives you a clear picture upfront — who's at risk, why, and what to do next."
            </p>
            <div className="marketing-quote-card__author">Stavyn Team</div>
          </div>
        </FadeIn>
      </MarketingSection>
    </PageWrapper>
  );
}
